import React, { useState, useEffect } from 'react';
import { fetchHardCrabCalendar } from '../services/api';
import DateRangeFilter from '../components/DateRangeFilter';

function toApiDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

const todayIso = new Date().toISOString().split('T')[0];

export default function HardCrabCalendar() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [start, setStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [end, setEnd] = useState(todayIso);

  const load = () => {
    setLoading(true);
    setError(null);
    fetchHardCrabCalendar(toApiDate(start), toApiDate(end))
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const rows = Array.isArray(data) ? data : data?.data ?? data?.records?.data ?? [];
  const maxCount = rows.length ? Math.max(...rows.map(r => Number(r.count ?? r.totalCount ?? r.orderCount ?? 0))) : 1;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Hard Crab Calendar</h2>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Date Range</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <DateRangeFilter start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e); }} />
          <button onClick={load} className="btn btn-primary" style={{ fontSize: 11 }}>Run Report</button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">Hard Crab Orders by Date {rows.length > 0 && `(${rows.length} days)`}</div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No hard crab order data found.</div>
        )}
        {rows.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Date</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Day</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Orders</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Revenue</th>
                  <th style={{ padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Volume</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const count = Number(row.count ?? row.totalCount ?? row.orderCount ?? 0);
                  const barPct = maxCount ? (count / maxCount * 100) : 0;
                  const date = row.date ?? row.shipDate ?? row.orderDate;
                  const dateObj = date ? new Date(date) : null;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                      <td style={{ padding: '8px 12px', color: 'var(--navy)', fontWeight: 600 }}>
                        {dateObj ? dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </td>
                      <td style={{ padding: '8px 8px', color: 'var(--gray-500)' }}>
                        {dateObj ? dateObj.toLocaleDateString('en-US', { weekday: 'short' }) : '—'}
                      </td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 700, color: 'var(--orange)' }}>{count.toLocaleString()}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right' }}>${Number(row.revenue ?? row.totalOrderAmount ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <div style={{ background: 'var(--gray-100)', borderRadius: 4, height: 8, width: '100%', overflow: 'hidden' }}>
                          <div style={{ background: 'var(--orange)', height: '100%', width: `${barPct}%`, borderRadius: 4 }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
